import {
	BlockControls,
	MediaPlaceholder,
	// @ts-ignore
	MediaReplaceFlow,
	RichText,
} from "@wordpress/block-editor";
import { withNotices } from "@wordpress/components";
import { __ } from "@wordpress/i18n";
import React from "react";
import {
	useBlockStyle,
	useClientId,
	useCopyPasteStyles,
	useDeviceType,
} from "../../hooks";
import { EditProps } from "../../types";
import { cn, noop } from "../../utils";
import InspectorControls from "./components/InspectorControls";
import Separator from "./components/Separator";
import "./style.scss";

interface Props extends EditProps<any> {
	noticeUI: any;
	noticeOperations: any;
	name: string;
}

const Edit: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			image = {},
			cssID,
			className,
			caption,
			enableCaption,
			overlay,
			overlayContent,
			showHeadingOn,
			overlayContentPosition,
			separatorStyle,
			separatorPosition,
			showSeparatorOn,
			showDescription,
			hoverEffect,
			imageFilter,
			maskShape,
			maskSize,
			maskPosition,
			maskRepeat,
		},
		setAttributes,
		noticeUI,
		noticeOperations,
	} = props;

	const { clientId } = useClientId(props);
	const { deviceType } = useDeviceType();
	const { CopyPasterStyleBlockControl } = useCopyPasteStyles();
	const { Style } = useBlockStyle({
		blockName: "image",
		clientId,
		attributes: props.attributes,
		deviceType,
	});

	const onError = (message: string) => {
		noticeOperations.removeAllNotices();
		noticeOperations.createErrorNotice(message);
	};

	const onSelect = (media: any) => {
		if (!media?.url) {
			return;
		}
		const { url: u, id: i, height: h, width: w, alt: a } = media;
		setAttributes({
			image: { url: u, id: i, height: h, width: w, alt: a },
		});
	};

	const onSelectURL = (u: string) => {
		setAttributes({
			image: {
				url: u,
				id: undefined,
				height: undefined,
				width: undefined,
				alt: undefined,
			},
		});
	};

	return (
		<>
			<InspectorControls
				attributes={props.attributes}
				setAttributes={props.setAttributes}
			/>
			{/* @ts-ignore */}
			<BlockControls group="block">
				<MediaReplaceFlow
					mediaId={image?.id}
					mediaURL={image?.url}
					allowedTypes={["image"]}
					accept="image/*"
					onSelect={onSelect}
					onSelectURL={onSelectURL}
					onError={onError}
				/>
			</BlockControls>
			<CopyPasterStyleBlockControl withBlockControls />
			<Style />
			<figure
				id={cssID ? cssID : null}
				className={cn(
					`mzb-image mzb-image-${clientId}`,
					overlay ? `overlay` : "",
					className
				)}
			>
				{image?.url ? (
					<>
						<img
							className={cn({
								[`hover-effect-${hoverEffect}`]: !!hoverEffect,
								[`filter-${imageFilter}`]: !!imageFilter,
								[`mask mask-${maskShape}`]: !!maskShape,
								[`mask-size-${maskSize}`]:
									maskShape &&
									maskShape !== "none" &&
									maskSize,
								[`mask-position-${maskPosition}`]:
									maskShape &&
									maskShape !== "none" &&
									!!maskPosition,
								[`mask-repeat-${maskRepeat}`]:
									maskShape &&
									maskShape !== "none" &&
									!!maskRepeat,
							})}
							src={image?.url}
							alt={image?.alt ?? ""}
						/>
						{!overlay && enableCaption && (
							<RichText
								tagName="figcaption"
								placeholder="Add Caption"
								value={caption}
								onChange={(val) =>
									setAttributes({ caption: val })
								}
							/>
						)}
						{overlay && (
							<div
								className={cn(
									`overlay-content ${overlayContentPosition}`,
									`show-heading-${showHeadingOn}`,
									`show-description-${showDescription}`
								)}
							>
								{separatorPosition === "before-title" && (
									<Separator
										style={separatorStyle}
										showOn={showSeparatorOn}
									/>
								)}
								<RichText
									tagName="h2"
									value={overlayContent}
									onChange={(value) =>
										setAttributes({ overlayContent: value })
									}
									placeholder={__(
										"Add Heading",
										"magazine-blocks"
									)}
								/>
								{separatorPosition === "after-title" && (
									<Separator
										style={separatorStyle}
										showOn={showSeparatorOn}
									/>
								)}
								<RichText
									tagName="figcaption"
									placeholder="Add Caption"
									value={caption}
									onChange={(val) =>
										setAttributes({ caption: val })
									}
								/>
								{separatorPosition === "after-sub-title" && (
									<Separator
										style={separatorStyle}
										showOn={showSeparatorOn}
									/>
								)}
							</div>
						)}
					</>
				) : (
					<MediaPlaceholder
						onSelect={onSelect}
						notices={noticeUI}
						onError={onError}
						accept="image/*"
						allowedTypes={["image"]}
						value={image?.id}
						onSelectURL={onSelectURL}
						onHTMLDrop={noop}
					/>
				)}
			</figure>
		</>
	);
};

export default withNotices(Edit);
